import React, { useState, useEffect, useCallback } from 'react';
import ConfirmModal from './ConfirmModal';

const getToken = () => localStorage.getItem('token');

const PUNISHMENT_METRIC_OPTIONS = [
  { value: 'punishments_discord', label: 'Наказания Discord' },
  { value: 'punishments_as', label: 'Наказания AS' },
];

const CHAT_METRIC_OPTIONS = [
  { value: 'chat_responses_discord', label: 'Ответы в Discord' },
  { value: 'chat_responses_as', label: 'Ответы в AS' },
];

const metricShort = (options, value) =>
  options.find((o) => o.value === value)?.label || value;

export default function SettingsStatsIngest({ isActive, setError, setSuccess }) {
  const [loading, setLoading] = useState(false);
  const [punishmentRules, setPunishmentRules] = useState([]);
  const [chatRules, setChatRules] = useState([]);
  const [expandedPun, setExpandedPun] = useState(null);
  const [expandedChat, setExpandedChat] = useState(null);
  const [draftPun, setDraftPun] = useState(null);
  const [draftChat, setDraftChat] = useState(null);
  const [newChPun, setNewChPun] = useState({ channel_id: '', label: '' });
  const [newChChat, setNewChChat] = useState({ channel_id: '', label: '', source_guild_id: '' });

  const [showPunModal, setShowPunModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  const [newPun, setNewPun] = useState({
    display_name: '',
    feed_channel_id: '',
    problems_channel_id: '',
    target_metric: 'punishments_discord',
    enabled: true,
  });
  const [newChat, setNewChat] = useState({
    display_name: '',
    target_metric: 'chat_responses_discord',
    require_reply: false,
    enabled: true,
  });

  const flashSuccess = useCallback(
    (msg) => {
      setSuccess(msg);
      setTimeout(() => setSuccess(''), 2200);
    },
    [setSuccess],
  );

  const openConfirm = useCallback((title, message, onConfirm) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm: async () => {
        try {
          await onConfirm();
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      },
    });
  }, []);

  const fetchLists = useCallback(async () => {
    const res = await fetch('/api/stats-ingest', {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Не удалось загрузить учёт');
    return {
      punishmentRules: Array.isArray(data.punishmentRules) ? data.punishmentRules : [],
      chatRules: Array.isArray(data.chatRules) ? data.chatRules : [],
    };
  }, []);

  /** Полная загрузка (вкладка / сброс раскрытия). */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const lists = await fetchLists();
      setPunishmentRules(lists.punishmentRules);
      setChatRules(lists.chatRules);
      setExpandedPun(null);
      setExpandedChat(null);
      setDraftPun(null);
      setDraftChat(null);
      setNewChPun({ channel_id: '', label: '' });
      setNewChChat({ channel_id: '', label: '', source_guild_id: '' });
    } catch (e) {
      setError(e.message);
      setTimeout(() => setError(''), 4000);
    } finally {
      setLoading(false);
    }
  }, [fetchLists, setError]);

  /** Обновить списки, не сворачивая открытое правило и не сбрасывая черновик полей. */
  const refreshLists = useCallback(async () => {
    try {
      const lists = await fetchLists();
      setPunishmentRules(lists.punishmentRules);
      setChatRules(lists.chatRules);
      setExpandedPun((id) =>
        id && lists.punishmentRules.some((r) => r.id === id) ? id : null,
      );
      setExpandedChat((id) =>
        id && lists.chatRules.some((r) => r.id === id) ? id : null,
      );
      setDraftPun((d) => {
        if (!d) return null;
        return lists.punishmentRules.some((r) => r.id === d.ruleId) ? d : null;
      });
      setDraftChat((d) => {
        if (!d) return null;
        return lists.chatRules.some((r) => r.id === d.ruleId) ? d : null;
      });
      return lists;
    } catch (e) {
      setError(e.message);
      setTimeout(() => setError(''), 4000);
      return null;
    }
  }, [fetchLists, setError]);

  useEffect(() => {
    if (isActive) load();
  }, [isActive, load]);

  const upsertChannel = (channels, channel) => {
    const list = Array.isArray(channels) ? [...channels] : [];
    const idx = list.findIndex((c) => String(c.channel_id) === String(channel.channel_id));
    if (idx >= 0) list[idx] = { ...list[idx], ...channel };
    else list.push(channel);
    return list;
  };

  const togglePun = (rule) => {
    if (expandedPun === rule.id) {
      setExpandedPun(null);
      setDraftPun(null);
      setNewChPun({ channel_id: '', label: '' });
      return;
    }
    setExpandedPun(rule.id);
    setDraftPun({
      ruleId: rule.id,
      display_name: rule.display_name,
      feed_channel_id: rule.feed_channel_id,
      problems_channel_id: rule.problems_channel_id || '',
      target_metric: rule.target_metric,
      enabled: rule.enabled !== false,
    });
    setNewChPun({ channel_id: '', label: '' });
  };

  const toggleChat = (rule) => {
    if (expandedChat === rule.id) {
      setExpandedChat(null);
      setDraftChat(null);
      setNewChChat({ channel_id: '', label: '', source_guild_id: '' });
      return;
    }
    setExpandedChat(rule.id);
    setDraftChat({
      ruleId: rule.id,
      display_name: rule.display_name,
      target_metric: rule.target_metric,
      require_reply: !!rule.require_reply,
      enabled: rule.enabled !== false,
    });
    setNewChChat({ channel_id: '', label: '' });
  };

  const patchPunEnabled = async (ruleId, enabled) => {
    try {
      const res = await fetch(`/api/stats-ingest/punishment-rules/${ruleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ enabled: !!enabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не сохранено');
      setPunishmentRules((prev) => prev.map((r) => (r.id === ruleId ? { ...r, enabled: !!enabled } : r)));
      setDraftPun((d) => (d && d.ruleId === ruleId ? { ...d, enabled: !!enabled } : d));
    } catch (e) {
      setError(e.message);
    }
  };

  const patchChatEnabled = async (ruleId, enabled) => {
    try {
      const res = await fetch(`/api/stats-ingest/chat-rules/${ruleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ enabled: !!enabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не сохранено');
      setChatRules((prev) => prev.map((r) => (r.id === ruleId ? { ...r, enabled: !!enabled } : r)));
      setDraftChat((d) => (d && d.ruleId === ruleId ? { ...d, enabled: !!enabled } : d));
    } catch (e) {
      setError(e.message);
    }
  };

  const savePunDraft = async (ruleId) => {
    try {
      const form = (({ ruleId: _id, ...rest }) => rest)(draftPun);
      const res = await fetch(`/api/stats-ingest/punishment-rules/${ruleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          display_name: form.display_name.trim(),
          feed_channel_id: form.feed_channel_id.trim(),
          problems_channel_id: (form.problems_channel_id || '').trim() || null,
          target_metric: form.target_metric,
          enabled: !!form.enabled,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не сохранено');
      flashSuccess('Правило сохранено');
      setPunishmentRules((prev) =>
        prev.map((r) =>
          r.id === ruleId
            ? {
                ...r,
                display_name: form.display_name.trim(),
                feed_channel_id: form.feed_channel_id.trim(),
                problems_channel_id: (form.problems_channel_id || '').trim() || null,
                target_metric: form.target_metric,
                enabled: !!form.enabled,
              }
            : r,
        ),
      );
    } catch (e) {
      setError(e.message);
    }
  };

  const saveChatDraft = async (ruleId) => {
    try {
      const form = (({ ruleId: _id, ...rest }) => rest)(draftChat);
      const res = await fetch(`/api/stats-ingest/chat-rules/${ruleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          display_name: form.display_name.trim(),
          target_metric: form.target_metric,
          require_reply: !!form.require_reply,
          enabled: !!form.enabled,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не сохранено');
      flashSuccess('Правило сохранено');
      setChatRules((prev) =>
        prev.map((r) =>
          r.id === ruleId
            ? {
                ...r,
                display_name: form.display_name.trim(),
                target_metric: form.target_metric,
                require_reply: !!form.require_reply,
                enabled: !!form.enabled,
              }
            : r,
        ),
      );
    } catch (e) {
      setError(e.message);
    }
  };

  const addPunRule = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/stats-ingest/punishment-rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          display_name: newPun.display_name.trim(),
          feed_channel_id: newPun.feed_channel_id.trim(),
          problems_channel_id: newPun.problems_channel_id.trim() || null,
          target_metric: newPun.target_metric,
          enabled: !!newPun.enabled,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не создано');
      flashSuccess('Правило создано — добавьте каналы, откуда читать');
      setShowPunModal(false);
      setNewPun({
        display_name: '',
        feed_channel_id: '',
        problems_channel_id: '',
        target_metric: 'punishments_discord',
        enabled: true,
      });
      const lists = await refreshLists();
      const rule = lists?.punishmentRules?.find((r) => r.id === data.id);
      if (rule) {
        setExpandedPun(rule.id);
        setDraftPun({
          ruleId: rule.id,
          display_name: rule.display_name,
          feed_channel_id: rule.feed_channel_id,
          problems_channel_id: rule.problems_channel_id || '',
          target_metric: rule.target_metric,
          enabled: rule.enabled !== false,
        });
        setNewChPun({ channel_id: '', label: '' });
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const addChatRule = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/stats-ingest/chat-rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          display_name: newChat.display_name.trim(),
          target_metric: newChat.target_metric,
          require_reply: !!newChat.require_reply,
          enabled: !!newChat.enabled,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не создано');
      flashSuccess('Правило создано — добавьте каналы, откуда читать');
      setShowChatModal(false);
      setNewChat({
        display_name: '',
        target_metric: 'chat_responses_discord',
        require_reply: false,
        enabled: true,
      });
      const lists = await refreshLists();
      const rule = lists?.chatRules?.find((r) => r.id === data.id);
      if (rule) {
        setExpandedChat(rule.id);
        setDraftChat({
          ruleId: rule.id,
          display_name: rule.display_name,
          target_metric: rule.target_metric,
          require_reply: !!rule.require_reply,
          enabled: rule.enabled !== false,
        });
        setNewChChat({ channel_id: '', label: '', source_guild_id: '' });
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const addChannel = async (e, kind, ruleId) => {
    e.preventDefault();
    const draft = kind === 'pun' ? newChPun : newChChat;
    const path =
      kind === 'pun'
        ? `/api/stats-ingest/punishment-rules/${ruleId}/channels`
        : `/api/stats-ingest/chat-rules/${ruleId}/channels`;
    const channel_id = draft.channel_id.trim();
    const label = draft.label.trim() || null;
    const source_guild_id =
      kind === 'chat' ? String(draft.source_guild_id || '').trim() : '';
    try {
      const res = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          channel_id,
          label: label || undefined,
          ...(kind === 'chat' && source_guild_id ? { source_guild_id } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не добавлено');
      flashSuccess('Канал добавлен');
      const channel = {
        channel_id,
        label,
        enabled: true,
        ...(kind === 'chat' ? { source_guild_id: source_guild_id || null } : {}),
      };
      if (kind === 'pun') {
        setNewChPun({ channel_id: '', label: '' });
        setPunishmentRules((prev) =>
          prev.map((r) => (r.id === ruleId ? { ...r, channels: upsertChannel(r.channels, channel) } : r)),
        );
      } else {
        setNewChChat({ channel_id: '', label: '', source_guild_id: '' });
        setChatRules((prev) =>
          prev.map((r) => (r.id === ruleId ? { ...r, channels: upsertChannel(r.channels, channel) } : r)),
        );
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const removeChannel = async (kind, ruleId, channelId) => {
    const path =
      kind === 'pun'
        ? `/api/stats-ingest/punishment-rules/${ruleId}/channels/${channelId}`
        : `/api/stats-ingest/chat-rules/${ruleId}/channels/${channelId}`;
    try {
      const res = await fetch(path, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не удалено');
      flashSuccess('Канал убран');
      if (kind === 'pun') {
        setPunishmentRules((prev) =>
          prev.map((r) =>
            r.id === ruleId
              ? {
                  ...r,
                  channels: (r.channels || []).filter((c) => String(c.channel_id) !== String(channelId)),
                }
              : r,
          ),
        );
      } else {
        setChatRules((prev) =>
          prev.map((r) =>
            r.id === ruleId
              ? {
                  ...r,
                  channels: (r.channels || []).filter((c) => String(c.channel_id) !== String(channelId)),
                }
              : r,
          ),
        );
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteRule = async (kind, ruleId) => {
    const path =
      kind === 'pun'
        ? `/api/stats-ingest/punishment-rules/${ruleId}`
        : `/api/stats-ingest/chat-rules/${ruleId}`;
    try {
      const res = await fetch(path, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не удалось удалить');
      flashSuccess('Правило удалено');
      if (kind === 'pun') {
        setPunishmentRules((prev) => prev.filter((r) => r.id !== ruleId));
        if (expandedPun === ruleId) {
          setExpandedPun(null);
          setDraftPun(null);
          setNewChPun({ channel_id: '', label: '' });
        }
      } else {
        setChatRules((prev) => prev.filter((r) => r.id !== ruleId));
        if (expandedChat === ruleId) {
          setExpandedChat(null);
          setDraftChat(null);
          setNewChChat({ channel_id: '', label: '', source_guild_id: '' });
        }
      }
    } catch (e) {
      setError(e.message);
    }
  };

  if (!isActive) return null;

  if (loading && punishmentRules.length === 0 && chatRules.length === 0) {
    return (
      <div className="settings-relay-loading loading relay-loading-wrap">
        <div className="relay-loading-spinner" aria-hidden />
        <span>Загрузка…</span>
      </div>
    );
  }

  return (
    <div className="settings-message-relay settings-relay-root stats-ingest-root">
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />

      {/* ——— Наказания ——— */}
      <section className="settings-form-card relay-card relay-card-sources settings-relay-folders-card">
        <div className="card-header settings-relay-folders-heading relay-card-head-row">
          <div className="relay-card-head-main">
            <h3 className="relay-card-h">Наказания</h3>
            <p className="stats-ingest-card-hint">
              Источник → лента на проверку → после одобрения в столбец статистики
            </p>
          </div>
          <button
            type="button"
            className="btn-add-server btn-relay-create relay-btn-accent"
            onClick={() => setShowPunModal(true)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>Добавить правило</span>
          </button>
        </div>

        <div className="settings-relay-folder-list">
          {punishmentRules.length === 0 ? (
            <div className="relay-empty-state relay-empty-state--minimal">
              Пока нет правил. Добавьте, откуда читать наказания.
            </div>
          ) : (
            punishmentRules.map((rule) => {
              const open = expandedPun === rule.id;
              const chCount = (rule.channels || []).filter((ch) => ch.enabled !== false).length;
              return (
                <div key={rule.id} className={`relay-folder-sheet ${open ? 'relay-folder-open' : ''}`}>
                  <div className="relay-folder-header-row">
                    <button
                      type="button"
                      className="relay-folder-bar"
                      onClick={() => togglePun(rule)}
                      aria-expanded={open}
                    >
                      <div className="relay-folder-bar-main">
                        <div className="relay-folder-title-row">
                          <span className="relay-folder-title">{rule.display_name}</span>
                          <span className="relay-folder-chips">
                            <span className="relay-chip-mini">{chCount} кан.</span>
                          </span>
                        </div>
                        <span className="relay-folder-meta">
                          → {metricShort(PUNISHMENT_METRIC_OPTIONS, rule.target_metric)}
                        </span>
                      </div>
                      <svg
                        className={`relay-chevron ${open ? 'relay-chevron-open' : ''}`}
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                    <div
                      className="relay-folder-side-controls"
                      onClick={(ev) => ev.stopPropagation()}
                      role="presentation"
                    >
                      <label className="relay-source-toggle-label" htmlFor={`pun-en-${rule.id}`}>
                        <input
                          id={`pun-en-${rule.id}`}
                          type="checkbox"
                          className="display-settings-toggle relay-source-toggle"
                          aria-label="Включить правило"
                          checked={rule.enabled !== false}
                          onChange={(e) => patchPunEnabled(rule.id, e.target.checked)}
                        />
                      </label>
                    </div>
                  </div>

                  {open && draftPun && draftPun.ruleId === rule.id && (
                    <div className="relay-folder-panel">
                      <div className="form-grid relay-folder-grid-top">
                        <div className="form-group">
                          <label htmlFor={`pun-name-${rule.id}`}>Название</label>
                          <input
                            id={`pun-name-${rule.id}`}
                            value={draftPun.display_name}
                            placeholder="Например: Основной сервер"
                            onChange={(e) => setDraftPun((d) => ({ ...d, display_name: e.target.value }))}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor={`pun-feed-${rule.id}`}>Канал ленты</label>
                          <input
                            id={`pun-feed-${rule.id}`}
                            value={draftPun.feed_channel_id}
                            inputMode="numeric"
                            placeholder="ID канала Discord"
                            onChange={(e) => setDraftPun((d) => ({ ...d, feed_channel_id: e.target.value }))}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor={`pun-prob-${rule.id}`}>Канал предупреждений</label>
                          <input
                            id={`pun-prob-${rule.id}`}
                            value={draftPun.problems_channel_id}
                            inputMode="numeric"
                            placeholder="Необязательно"
                            onChange={(e) =>
                              setDraftPun((d) => ({ ...d, problems_channel_id: e.target.value }))
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor={`pun-metric-${rule.id}`}>Столбец после одобрения</label>
                          <select
                            id={`pun-metric-${rule.id}`}
                            value={draftPun.target_metric}
                            onChange={(e) => setDraftPun((d) => ({ ...d, target_metric: e.target.value }))}
                          >
                            {PUNISHMENT_METRIC_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="relay-folder-actions-row">
                        <button
                          type="button"
                          className="btn btn-primary relay-folder-btn-save"
                          onClick={() => savePunDraft(rule.id)}
                        >
                          Сохранить изменения
                        </button>
                        <button
                          type="button"
                          className="relay-folder-btn-remove"
                          onClick={() =>
                            openConfirm(
                              'Удаление правила',
                              `Удалить «${rule.display_name}» и все его каналы?`,
                              () => deleteRule('pun', rule.id),
                            )
                          }
                        >
                          Удалить правило
                        </button>
                      </div>

                      <div className="relay-channels-section relay-subpanel">
                        <h4 className="relay-subheading relay-subheading-inline">Откуда читать</h4>
                        <p className="relay-hint-muted stats-ingest-field-hint stats-ingest-field-hint--block">
                          Каналы логов наказаний (embeds / .ban / .mute)
                        </p>
                        {(rule.channels || []).length === 0 ? (
                          <p className="relay-hint-muted">Нет каналов</p>
                        ) : (
                          <ul className="relay-channel-list">
                            {(rule.channels || []).map((ch) => (
                              <li key={ch.channel_id} className="relay-channel-item">
                                <code>{ch.channel_id}</code>
                                {ch.label ? <span className="relay-ch-label">{ch.label}</span> : null}
                                <button
                                  type="button"
                                  className="relay-btn-delete relay-ch-remove"
                                  onClick={() =>
                                    openConfirm(
                                      'Удаление канала',
                                      `Убрать канал ${ch.label || ch.channel_id} из правила?`,
                                      () => removeChannel('pun', rule.id, ch.channel_id),
                                    )
                                  }
                                >
                                  Убрать
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        <form className="relay-add-ch-form" onSubmit={(e) => addChannel(e, 'pun', rule.id)}>
                          <input
                            type="text"
                            aria-label="ID канала"
                            placeholder="ID канала (17-20 цифр)"
                            value={newChPun.channel_id}
                            inputMode="numeric"
                            onChange={(e) => setNewChPun((s) => ({ ...s, channel_id: e.target.value }))}
                            required
                          />
                          <input
                            type="text"
                            aria-label="Подпись"
                            placeholder="Название канала (необязательно)"
                            value={newChPun.label}
                            onChange={(e) => setNewChPun((s) => ({ ...s, label: e.target.value }))}
                          />
                          <button type="submit" className="btn btn-secondary relay-add-ch-submit">
                            Добавить
                          </button>
                        </form>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* ——— Ответы ——— */}
      <section className="settings-form-card relay-card relay-card-sources settings-relay-folders-card stats-ingest-section-gap">
        <div className="card-header settings-relay-folders-heading relay-card-head-row">
          <div className="relay-card-head-main">
            <h3 className="relay-card-h">Ответы в чатах</h3>
            <p className="stats-ingest-card-hint">
              Сообщения менеджеров в каналах <strong>рабочего Discord</strong> (где пишут игрокам), не Discord
              сайта/состава. Self-аккаунт должен быть на том сервере.
            </p>
          </div>
          <button
            type="button"
            className="btn-add-server btn-relay-create relay-btn-accent"
            onClick={() => setShowChatModal(true)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>Добавить правило</span>
          </button>
        </div>

        <div className="settings-relay-folder-list">
          {chatRules.length === 0 ? (
            <div className="relay-empty-state relay-empty-state--minimal">
              Пока нет правил. Добавьте, где считать ответы модераторов.
            </div>
          ) : (
            chatRules.map((rule) => {
              const open = expandedChat === rule.id;
              const chCount = (rule.channels || []).filter((ch) => ch.enabled !== false).length;
              return (
                <div key={rule.id} className={`relay-folder-sheet ${open ? 'relay-folder-open' : ''}`}>
                  <div className="relay-folder-header-row">
                    <button
                      type="button"
                      className="relay-folder-bar"
                      onClick={() => toggleChat(rule)}
                      aria-expanded={open}
                    >
                      <div className="relay-folder-bar-main">
                        <div className="relay-folder-title-row">
                          <span className="relay-folder-title">{rule.display_name}</span>
                          <span className="relay-folder-chips">
                            <span className="relay-chip-mini">{chCount} кан.</span>
                          </span>
                        </div>
                        <span className="relay-folder-meta">
                          → {metricShort(CHAT_METRIC_OPTIONS, rule.target_metric)}
                          {rule.require_reply ? ' · только reply' : ''}
                        </span>
                      </div>
                      <svg
                        className={`relay-chevron ${open ? 'relay-chevron-open' : ''}`}
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                    <div
                      className="relay-folder-side-controls"
                      onClick={(ev) => ev.stopPropagation()}
                      role="presentation"
                    >
                      <label className="relay-source-toggle-label" htmlFor={`chat-en-${rule.id}`}>
                        <input
                          id={`chat-en-${rule.id}`}
                          type="checkbox"
                          className="display-settings-toggle relay-source-toggle"
                          aria-label="Включить правило"
                          checked={rule.enabled !== false}
                          onChange={(e) => patchChatEnabled(rule.id, e.target.checked)}
                        />
                      </label>
                    </div>
                  </div>

                  {open && draftChat && draftChat.ruleId === rule.id && (
                    <div className="relay-folder-panel">
                      <div className="form-grid relay-folder-grid-top">
                        <div className="form-group">
                          <label htmlFor={`chat-name-${rule.id}`}>Название</label>
                          <input
                            id={`chat-name-${rule.id}`}
                            value={draftChat.display_name}
                            placeholder="Например: Чат помощи"
                            onChange={(e) => setDraftChat((d) => ({ ...d, display_name: e.target.value }))}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor={`chat-metric-${rule.id}`}>Столбец статистики</label>
                          <select
                            id={`chat-metric-${rule.id}`}
                            value={draftChat.target_metric}
                            onChange={(e) => setDraftChat((d) => ({ ...d, target_metric: e.target.value }))}
                          >
                            {CHAT_METRIC_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="relay-modal-toggle-row stats-ingest-toggle-row">
                        <span className="relay-modal-toggle-text">Считать только ответы (reply)</span>
                        <input
                          type="checkbox"
                          className="display-settings-toggle"
                          aria-label="Считать только ответы"
                          checked={!!draftChat.require_reply}
                          onChange={(e) =>
                            setDraftChat((d) => ({ ...d, require_reply: e.target.checked }))
                          }
                        />
                      </div>

                      <div className="relay-folder-actions-row">
                        <button
                          type="button"
                          className="btn btn-primary relay-folder-btn-save"
                          onClick={() => saveChatDraft(rule.id)}
                        >
                          Сохранить изменения
                        </button>
                        <button
                          type="button"
                          className="relay-folder-btn-remove"
                          onClick={() =>
                            openConfirm(
                              'Удаление правила',
                              `Удалить «${rule.display_name}» и все его каналы?`,
                              () => deleteRule('chat', rule.id),
                            )
                          }
                        >
                          Удалить правило
                        </button>
                      </div>

                      <div className="relay-channels-section relay-subpanel">
                        <h4 className="relay-subheading relay-subheading-inline">Откуда читать</h4>
                        <p className="relay-hint-muted stats-ingest-field-hint stats-ingest-field-hint--block">
                          ID канала с <strong>рабочего</strong> Discord (ПКМ → Copy Channel ID). Рекомендуется
                          указать ID сервера (ПКМ по иконке сервера → Copy Server ID).
                        </p>
                        {(rule.channels || []).length === 0 ? (
                          <p className="relay-hint-muted">Нет каналов</p>
                        ) : (
                          <ul className="relay-channel-list">
                            {(rule.channels || []).map((ch) => (
                              <li key={ch.channel_id} className="relay-channel-item">
                                <code>{ch.channel_id}</code>
                                {ch.source_guild_id ? (
                                  <span className="relay-ch-label">guild {ch.source_guild_id}</span>
                                ) : null}
                                {ch.label ? <span className="relay-ch-label">{ch.label}</span> : null}
                                <button
                                  type="button"
                                  className="relay-btn-delete relay-ch-remove"
                                  onClick={() =>
                                    openConfirm(
                                      'Удаление канала',
                                      `Убрать канал ${ch.label || ch.channel_id} из правила?`,
                                      () => removeChannel('chat', rule.id, ch.channel_id),
                                    )
                                  }
                                >
                                  Убрать
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        <form className="relay-add-ch-form" onSubmit={(e) => addChannel(e, 'chat', rule.id)}>
                          <input
                            type="text"
                            aria-label="ID канала"
                            placeholder="ID канала (рабочий Discord)"
                            value={newChChat.channel_id}
                            inputMode="numeric"
                            onChange={(e) => setNewChChat((s) => ({ ...s, channel_id: e.target.value }))}
                            required
                          />
                          <input
                            type="text"
                            aria-label="ID сервера Discord"
                            placeholder="ID сервера Discord (рекомендуется)"
                            value={newChChat.source_guild_id}
                            inputMode="numeric"
                            onChange={(e) =>
                              setNewChChat((s) => ({ ...s, source_guild_id: e.target.value }))
                            }
                          />
                          <input
                            type="text"
                            aria-label="Подпись"
                            placeholder="Название канала (необязательно)"
                            value={newChChat.label}
                            onChange={(e) => setNewChChat((s) => ({ ...s, label: e.target.value }))}
                          />
                          <button type="submit" className="btn btn-secondary relay-add-ch-submit">
                            Добавить
                          </button>
                        </form>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      {showPunModal && (
        <div className="form-modal-overlay relay-modal-overlay" onClick={() => setShowPunModal(false)}>
          <div
            className="form-modal relay-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="stats-pun-modal-title"
          >
            <div className="form-modal-header relay-modal-top">
              <div>
                <h3 id="stats-pun-modal-title">Правило наказаний</h3>
              </div>
              <button
                type="button"
                className="btn-close-form"
                onClick={() => setShowPunModal(false)}
                title="Закрыть"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <form onSubmit={addPunRule} className="form-modal-body relay-modal-body">
              <div className="form-group">
                <label htmlFor="new-pun-name">Название</label>
                <input
                  id="new-pun-name"
                  value={newPun.display_name}
                  placeholder="Например: Основной сервер"
                  onChange={(e) => setNewPun((s) => ({ ...s, display_name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-pun-feed">Канал ленты (проверка)</label>
                <input
                  id="new-pun-feed"
                  value={newPun.feed_channel_id}
                  inputMode="numeric"
                  placeholder="ID канала Discord"
                  onChange={(e) => setNewPun((s) => ({ ...s, feed_channel_id: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-pun-prob">Канал предупреждений</label>
                <input
                  id="new-pun-prob"
                  value={newPun.problems_channel_id}
                  inputMode="numeric"
                  placeholder="Необязательно"
                  onChange={(e) => setNewPun((s) => ({ ...s, problems_channel_id: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-pun-metric">Столбец статистики после одобрения</label>
                <select
                  id="new-pun-metric"
                  value={newPun.target_metric}
                  onChange={(e) => setNewPun((s) => ({ ...s, target_metric: e.target.value }))}
                >
                  {PUNISHMENT_METRIC_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <p className="relay-hint-muted">После создания раскройте правило и добавьте каналы-источники.</p>
              <div className="form-actions relay-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPunModal(false)}>
                  Отмена
                </button>
                <button type="submit" className="btn btn-primary">
                  Создать
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showChatModal && (
        <div className="form-modal-overlay relay-modal-overlay" onClick={() => setShowChatModal(false)}>
          <div
            className="form-modal relay-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="stats-chat-modal-title"
          >
            <div className="form-modal-header relay-modal-top">
              <div>
                <h3 id="stats-chat-modal-title">Правило ответов</h3>
              </div>
              <button
                type="button"
                className="btn-close-form"
                onClick={() => setShowChatModal(false)}
                title="Закрыть"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <form onSubmit={addChatRule} className="form-modal-body relay-modal-body">
              <div className="form-group">
                <label htmlFor="new-chat-name">Название</label>
                <input
                  id="new-chat-name"
                  value={newChat.display_name}
                  placeholder="Например: Чат помощи"
                  onChange={(e) => setNewChat((s) => ({ ...s, display_name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-chat-metric">Столбец статистики</label>
                <select
                  id="new-chat-metric"
                  value={newChat.target_metric}
                  onChange={(e) => setNewChat((s) => ({ ...s, target_metric: e.target.value }))}
                >
                  {CHAT_METRIC_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="relay-modal-toggle-row">
                <span className="relay-modal-toggle-text">Считать только ответы (reply)</span>
                <input
                  type="checkbox"
                  className="display-settings-toggle"
                  aria-label="Считать только ответы"
                  checked={!!newChat.require_reply}
                  onChange={(e) => setNewChat((s) => ({ ...s, require_reply: e.target.checked }))}
                />
              </div>
              <p className="relay-hint-muted">После создания раскройте правило и добавьте каналы-источники.</p>
              <div className="form-actions relay-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowChatModal(false)}>
                  Отмена
                </button>
                <button type="submit" className="btn btn-primary">
                  Создать
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
